import styles from 'css/src/components/pricing/PricingPlanBlock.module.scss';

import React from 'react';
import classNames from 'classnames';

import type { Plan } from 'js/src/client';
import type { BUTTON_VARIANTS } from 'js/src/components/common/ui/Button';
import PricingPlanHighlights from 'js/src/components/pricing/PricingPlanHighlights';
import PricingPlanInfo from 'js/src/components/pricing/PricingPlanInfo';
import type { PlanSwitchErrorInfo } from 'js/src/components/pricing/types';
import UpgradeButton from 'js/src/components/pricing/UpgradeButton';

const PricingPlanBlock = React.memo(function PricingPlanBlock({
    plan,
    switchToPlan,
    saleSubHeading,
    planSubHeading,
    handlePlanSwitchError,
    andMoreClicked,
    upgradeButtonVariant,
    topHighlight,
    compact = false,
}: {
    plan?: Plan;
    switchToPlan: (plan: Plan) => void;
    saleSubHeading?: string;
    planSubHeading?: string;
    handlePlanSwitchError: (error_info: PlanSwitchErrorInfo) => void;
    andMoreClicked: (e: React.MouseEvent) => void;
    upgradeButtonVariant?: BUTTON_VARIANTS;
    topHighlight?: string | number;
    // Renders the card for a standalone (single-card) context such as the in-app upsell
    // modal, collapsing the grid-alignment spacing. Defaults to the pricing-page grid layout.
    compact?: boolean;
}) {
    // the community plan is horizontally laid out on the page
    const horizontalLayout = plan?.price === 0;

    return (
        <div
            className={classNames(styles['price-block'], 'd-flex', {
                featured: plan?.featured,
                current: SERVER.USER && SERVER.USER.plan === plan?.name,
                [styles['enterprise']]: plan?.is_enterprise,
                [styles['horizontal-layout']]: horizontalLayout,
                [styles['compact']]: compact,
            })}
        >
            <PricingPlanInfo
                plan={plan}
                switchToPlan={switchToPlan}
                saleSubHeading={saleSubHeading}
                planSubHeading={planSubHeading}
                handlePlanSwitchError={handlePlanSwitchError}
                upgradeButtonVariant={upgradeButtonVariant}
                compact={compact}
            />
            {!horizontalLayout && !compact && <hr className="my-l5-24" />}
            <PricingPlanHighlights
                highlights={plan?.highlights}
                showAndMore={plan?.price !== 0 && !compact}
                andMoreClicked={andMoreClicked}
                className=""
                topHighlight={topHighlight}
            />
            {/* In compact mode the upgrade button lives at the bottom of the card, below
                the highlights, instead of up near the price. */}
            {compact && plan && (
                <div className={styles['compact-button']}>
                    <UpgradeButton
                        plan={plan}
                        switchToPlan={switchToPlan}
                        handlePlanSwitchError={handlePlanSwitchError}
                        className="w-100"
                        variant={upgradeButtonVariant}
                    />
                </div>
            )}
        </div>
    );
});

export default PricingPlanBlock;
